import React, {useEffect, useRef, useState} from 'react';
import Header from '../../components/header';
import Loader from '../../../../components/spinner/Loader';
import {Field, Form, Formik} from 'formik';
import _get from 'lodash/get';
import {toast} from 'react-toastify';
import {Trans, useTranslation} from 'react-i18next';
import BooleanRadioButton from '../../../../components/formik/BooleanRadioButton';
import skincareProDao from '../../../../dao/skincareProDao';
import SameConsent from './components/SameConsent';
import DifferentConsent from './components/DifferentConsent';
import _isNil from 'lodash/isNil';
import smbUtils from '../../../../utils/smbUtils';
import SimpleButtonGroup from '../../../../components/formik/SimpleButtonGroup';
import {history} from '../../../../utils/redux/store';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import {useSelector} from 'react-redux';
import ProcessingInProgressModal from '../components/ProcessingInProgressModal';
import { isFunctionBusyError } from '../../../../utils/errorUtils';

export default function UserConsent() {
  const {t} = useTranslation();
  const formRef = useRef();
  const auth = useSelector(state => state.components.auth.accountSummary);
  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [isShowProcessingModal, setIsShowProcessingModal] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [initValue, setInitValue] = useState({
    single_template_for_all: true,
    user_consents: [
      {
        template_id: -1,
        store_ids: [],
      },
    ],
  });
  const [categories, setCategories] = useState({
    storeCategory: [],
    tempCategory: [],
  });
  const type = 'ScpUserConsentContent';
  const type2 = 'SmbUserConsent';

  const formikConfig = {
    enableReinitialize: true,
    initialValues: initValue,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true);
      if (
        !privilegeUtils.canCustomizationConsent(auth) &&
        !values.single_template_for_all
      ) {
        toast.error(
          <Trans i18nKey="toast.error.customized.version.failed">
            If you need to set up a customized template, please go to Help >
            Contact Us.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      await skincareProDao
        .putCustomerSetting({
          type: type,
          use_default: false, //the setting is not for consent
          data: values,
        })
        .then(res => {
          if (res.status === 'success') {
            setIsShowPublishModal(true);
            resetForm(values);
            return;
          }
          toast.error(
            <Trans i18nKey="p.s.addoreditcategory.error.failed">Failed</Trans>
          );
        })
        .catch(e => {
          if (isFunctionBusyError(e)) {
            setIsShowProcessingModal(true);
            setSubmitting(false);
            return;
          }

          toast.error(
            <Trans i18nKey="p.s.addoreditcategory.error.failed">Failed</Trans>
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoading(true);
      try {
        const [allStores, listUserConsent, templateLists] = await Promise.all([
          skincareProDao.getAllStores(),
          skincareProDao.getCustomerSetting({
            type: type,
          }),
          skincareProDao.listUIConfigTemplates({
            template_type: type2,
          }),
        ]);
        const consentData = _get(listUserConsent, 'editing_revision.data');
        if (_isNil(_get(consentData, 'single_template_for_all'))) {
          toast.error(
            <Trans i18nKey="toast.error.loading.data.failed">
              Failed to load data.
            </Trans>
          );
          return;
        }
        setInitValue(consentData);
        setCategories(prevState => ({
          ...prevState,
          storeCategory: allStores.map(smbUtils.mapToCategory),
          tempCategory: [
            {
              label: (
                <Trans i18nKey="p.s.uiconfiguration.userconsnt.general">
                  General User Consent
                </Trans>
              ),
              value: -1,
            },
            ..._get(templateLists, 'templates', []).map(item => ({
              label: item.template_name,
              value: item.template_id,
            })),
          ],
        }));
        setIsInitialLoading(false);
      } catch (error) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
      }
    };
    fetchData();
  }, []);

  return isInitialLoading ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <>
      <Formik {...formikConfig} ref={formRef}>
        {formikProps => (
          <Form id="user-consent">
            <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
            <p>
              <Trans i18nKey="p.s.uiconfiguration.userconsnt.note">
                Currently, only the user consent for Skin Analysis can be
                customized. If you’re looking for customization for Aesthetic
                Simulation, please contact our sales team for more information
                and a tailored quote.
              </Trans>
            </p>
            <div>
              <h4 className="kt-margin-b-20">
                <Trans i18nKey="p.s.uiconfiguration.userconsnt.analysis.title">
                  User Consent Content - Skin Analysis
                </Trans>
                <div
                  className="kt-margin-t-10"
                  style={{
                    backgroundColor: '#e1e1ef',
                    height: '1px',
                  }}
                />
              </h4>
              <div>
                <div className="form-group row">
                  <div className="col-12">
                    <Trans i18nKey="p.s.uiconfiguration.userconsnt.desc">
                      Decide whether to apply the same consent content for all
                      customers or assign different content based on location.
                    </Trans>
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-3 col-md-2 text-left">
                    <label style={{paddingBottom: '10px'}}>
                      <Trans i18nKey="p.s.uiconfiguration.userconsnt.settings">
                        User Consent Settings
                      </Trans>
                    </label>
                  </div>
                  <div className="col-9 col-md-10 align-items-center">
                    <div className="row">
                      <div className="col-12 col-md-6 col-lg-5">
                        <Field
                          name="single_template_for_all"
                          label={t(
                            'p.s.uiconfiguration.userconsnt.settings.for.all.clients',
                            'Use the same user consent for all clients'
                          )}
                          value={true}
                          component={BooleanRadioButton}
                          onChange={() => {
                            formikProps.setFieldValue('user_consents', [
                              {
                                template_id: -1,
                              },
                            ]);
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-5">
                        <Field
                          name="single_template_for_all"
                          label={t(
                            'p.s.uiconfiguration.userconsnt.settings.for.diff.clients',
                            'Assign different user consent content per location'
                          )}
                          value={false}
                          component={BooleanRadioButton}
                          onChange={() => {
                            formikProps.setFieldValue(
                              'user_consents',
                              _get(categories, 'tempCategory', []).map(
                                item => ({
                                  template_id: item.value,
                                  store_ids: [],
                                })
                              )
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-group row">
                  <div className="col-3 col-md-2" />
                  <div className="col-9 col-md-10">
                    {_get(formikProps, 'values.single_template_for_all') && (
                      <SameConsent
                        isLoading={isInitialLoading}
                        categories={categories}
                        formikProps={formikProps}
                      />
                    )}
                    {!_get(formikProps, 'values.single_template_for_all') && (
                      <DifferentConsent
                        data={_get(formikProps, 'values.user_consents', [])}
                        isLoading={isInitialLoading}
                        categories={categories}
                        formikProps={formikProps}
                      />
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <SimpleButtonGroup
                    form={'user-consent'}
                    isUpdating={true}
                    onCancelClick={() =>
                      history.push('/skincare-pro/ui-configuration/overview')
                    }
                  />
                </div>
              </div>
            </div>
            <PublishToOnlineModal
              isShow={isShowPublishModal}
              onPublish={isPublish => {
                formikProps.setSubmitting(false);
                formikProps.resetForm(formikProps.values);
                setPublishStatus(isPublish);
                setIsShowPublishModal(false);
              }}
            />
            <ProcessingInProgressModal
              show={isShowProcessingModal}
              onHide={() => {
                setIsShowProcessingModal(false);
              }}
            />
          </Form>
        )}
      </Formik>
    </>
  );
}
